import { CREDIT_CONNECTION_PRICE_CREDITS } from './creditWalletRpc.js';

// Single source for credit-connection purchase denial/error copy, shared by
// the access-code management surface (CreditConnectionPurchase.js), the
// per-patient connect surface, and the New-Client form's connect step
// (MedicalScribeData.js). Pure copy mappers only — no React, no supabase.

// Soft denials return data {purchased: false, reason, ...}; map them to
// neutral copy. The RPC supplies the arithmetic for capacity denials.
export const purchaseDeniedMessage = (result) => {
  switch (result?.reason) {
    case 'already_connected_booking':
      return result.warning || 'This client is already connected through a booking; connections from bookings are free.';
    case 'already_connected':
      return result.funding === 'credits'
        ? 'This client is already connected through a credit connection.'
        : 'This client is already connected and billed through your plan; this purchase changes nothing.';
    case 'no_active_plan':
      // Dead-safe copy: the no_active_plan gate was removed from the purchase
      // RPC on 2026-08-30; kept for any older response still in flight.
      return 'An active plan is required to add a credit connection.';
    case 'capacity_reached':
      return result.arithmetic || 'Your plan has reached its limit of credit-paid connections.';
    case 'plan_ending':
      return 'Your plan is scheduled to end before this connection period completes. Renew or upgrade before adding a connection.';
    default:
      return null;
  }
};

// RPC errors: 22023 is either an invalid request (email shape) or the
// operation-id/email conflict (same id reused for a different email — the
// 409-style guard). 42501 is an auth failure. An empty wallet is not a soft
// denial: begin_operation raises P0001 'CW_INSUFFICIENT_CREDITS' and the
// purchase RPC lets it surface.
export const purchaseErrorMessage = (error) => {
  if (error?.code === '22023') {
    if ((error.message || '').includes('conflict')) {
      return 'This purchase attempt was already used for a different email. The attempt was reset — try again.';
    }
    return 'Enter a valid client email (3–320 characters, containing an @).';
  }
  if (error?.code === '42501') {
    return 'You must be signed in as a therapist to add a credit connection.';
  }
  if ((error?.message || '').includes('CW_INSUFFICIENT_CREDITS')) {
    return `You need ${CREDIT_CONNECTION_PRICE_CREDITS} credits for this connection. Check your credit balance and plan in Subscription settings.`;
  }
  return error?.message || 'The credit purchase could not be completed.';
};
